import { useState, useEffect, useCallback, useRef } from "react";
import { Platform } from "react-native";
import {
  isNotificationSupported,
  checkNotificationPermission,
  requestNotificationPermission,
  getNotificationTokenWithRetry,
  initializeNotificationHandlers,
  setupNotificationOpenListener,
  getInitialNotification,
  handleNotificationOpen,
  showTestNotification,
} from "../services/notifications/notificationService";
import { setDocument, update } from "../services/firestoreServices";
import { serverTimestamp } from "../firebase";
import { USERS_COLLECTION } from "../constants/collections";

export interface NotificationState {
  isSupported: boolean;
  permissionStatus:
    | "unknown"
    | "granted"
    | "denied"
    | "not_determined"
    | "error";
  fcmToken: string | null;
  isLoading: boolean;
  error: string | null;
}

export interface NotificationActions {
  requestPermission: () => Promise<boolean>;
  refreshToken: () => Promise<string | null>;
  testNotification: (title: string, body: string) => Promise<void>;
  checkPermission: () => Promise<void>;
}

export const useNotifications = (
  userId?: string,
  userName?: string,
): NotificationState & NotificationActions => {
  const [state, setState] = useState<NotificationState>({
    isSupported: false,
    permissionStatus: "unknown",
    fcmToken: null,
    isLoading: false,
    error: null,
  });

  const cleanupRef = useRef<(() => void) | null>(null);
  const openListenerCleanupRef = useRef<(() => void) | null>(null);

  // Check if notifications are supported
  useEffect(() => {
    const supported = isNotificationSupported();
    setState((prev) => ({ ...prev, isSupported: supported }));

    if (!supported) {
      setState((prev) => ({ ...prev, permissionStatus: "not_determined" }));
    }
  }, []);

  // Initialize notification handlers
  useEffect(() => {
    if (state.isSupported) {
      try {
        const cleanup = initializeNotificationHandlers();
        const openListenerCleanup = setupNotificationOpenListener();

        cleanupRef.current = cleanup;
        openListenerCleanupRef.current = openListenerCleanup;

        // Check for initial notification that opened the app
        getInitialNotification().then((remoteMessage) => {
          if (remoteMessage) {
            console.log("App opened from notification:", remoteMessage);
            void handleNotificationOpen(remoteMessage);
          }
        });

        return () => {
          if (cleanup) cleanup();
          if (openListenerCleanup) openListenerCleanup();
        };
      } catch (error) {
        console.error("Failed to initialize notification handlers:", error);
        setState((prev) => ({
          ...prev,
          error: "Failed to initialize notifications",
        }));
      }
    }
  }, [state.isSupported]);

  // Check current notification permission status
  const checkPermission = useCallback(async (): Promise<void> => {
    if (!state.isSupported) return;

    try {
      setState((prev) => ({ ...prev, isLoading: true, error: null }));

      const hasPermission = await checkNotificationPermission();
      const permissionStatus = hasPermission ? "granted" : "denied";

      setState((prev) => ({
        ...prev,
        permissionStatus,
        isLoading: false,
      }));
    } catch (error) {
      console.error("Error checking notification permission:", error);
      setState((prev) => ({
        ...prev,
        permissionStatus: "error",
        error: "Failed to check permission",
        isLoading: false,
      }));
    }
  }, [state.isSupported]);

  // Request notification permission
  const requestPermission = useCallback(async (): Promise<boolean> => {
    if (!state.isSupported) return false;

    try {
      setState((prev) => ({ ...prev, isLoading: true, error: null }));

      const result = await requestNotificationPermission();
      // Convert result to boolean: "granted" = true, anything else = false
      const granted = result === "granted" || result === true;
      const permissionStatus = granted ? "granted" : "denied";

      setState((prev) => ({
        ...prev,
        permissionStatus,
        isLoading: false,
      }));

      if (granted && userId) {
        // Store permission status in user profile
        try {
          await update(USERS_COLLECTION, userId, {
            notificationPermissionStatus: granted,
          });
        } catch (error) {
          console.error("Failed to update user permission status:", error);
        }

        // Get FCM token after permission is granted
        await refreshToken();
      }

      return granted;
    } catch (error) {
      console.error("Error requesting notification permission:", error);
      setState((prev) => ({
        ...prev,
        permissionStatus: "error",
        error: "Failed to request permission",
        isLoading: false,
      }));
      return false;
    }
  }, [state.isSupported, userId]);

  // Refresh FCM token
  const refreshToken = useCallback(async (): Promise<string | null> => {
    if (!state.isSupported || state.permissionStatus !== "granted") {
      return null;
    }

    try {
      setState((prev) => ({ ...prev, isLoading: true, error: null }));

      const token = await getNotificationTokenWithRetry();

      if (token) {
        setState((prev) => ({ ...prev, fcmToken: token, isLoading: false }));

        // Store token in Firestore if user ID is available
        if (userId) {
          try {
            await setDocument("pushTokens", token, {
              userId: userId,
              name: userName || "",
              createdAt: serverTimestamp(),
              lastUpdated: serverTimestamp(),
              platform: Platform.OS,
            });
          } catch (error) {
            console.error("Failed to store FCM token:", error);
            setState((prev) => ({
              ...prev,
              error: "Failed to store token",
            }));
          }
        }

        return token;
      } else {
        setState((prev) => ({
          ...prev,
          error: "Failed to retrieve FCM token",
          isLoading: false,
        }));
        return null;
      }
    } catch (error) {
      console.error("Error refreshing FCM token:", error);
      setState((prev) => ({
        ...prev,
        error: "Failed to refresh token",
        isLoading: false,
      }));
      return null;
    }
  }, [state.isSupported, state.permissionStatus, userId, userName]);

  // Test notification
  const testNotification = useCallback(
    async (title: string, body: string): Promise<void> => {
      if (!state.isSupported || state.permissionStatus !== "granted") {
        throw new Error(
          "Notifications not supported or permission not granted",
        );
      }

      try {
        setState((prev) => ({ ...prev, isLoading: true, error: null }));

        await showTestNotification(title, body);

        setState((prev) => ({ ...prev, isLoading: false }));
      } catch (error) {
        console.error("Error showing test notification:", error);
        setState((prev) => ({
          ...prev,
          error: "Failed to show test notification",
          isLoading: false,
        }));
        throw error;
      }
    },
    [state.isSupported, state.permissionStatus],
  );

  // Auto-check permission on mount
  useEffect(() => {
    if (state.isSupported) {
      checkPermission();
    }
  }, [state.isSupported, checkPermission]);

  // Auto-refresh token when permission is granted
  useEffect(() => {
    if (state.permissionStatus === "granted" && !state.fcmToken) {
      refreshToken();
    }
  }, [state.permissionStatus, state.fcmToken, refreshToken]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (cleanupRef.current) {
        cleanupRef.current();
      }
      if (openListenerCleanupRef.current) {
        openListenerCleanupRef.current();
      }
    };
  }, []);

  return {
    ...state,
    requestPermission,
    refreshToken,
    testNotification,
    checkPermission,
  };
};
